import type { Database, Json } from "~~/types/supabase";

// Self-service for visitors (docs/superpowers/specs/2026-10-22-self-service-design.md): the
// organization's default in organizations.shop_settings.self_service, overridden per key by
// events.additional_settings.self_service. Read with the same defaults and clamps as
// self_service_settings() in SQL, which decides; this only fills the forms.
export interface SelfServiceSettings {
  enabled: boolean;
  // names and answers on the tickets, until this many minutes before the timeslot starts
  edit: { enabled: boolean; closes_before_minutes: number };
  // cancelling tickets, with money back only when refund is on (there is no legal right to it)
  cancel: { enabled: boolean; refund: boolean; closes_before_minutes: number };
}

// Until the start, an hour, a day or a week before it
export const SELF_SERVICE_DEADLINES = [0, 60, 1440, 10080];
export const SELF_SERVICE_ON: SelfServiceSettings = {
  enabled: true,
  edit: { enabled: true, closes_before_minutes: 0 },
  cancel: { enabled: false, refund: false, closes_before_minutes: 10080 },
};

type RawAction = { enabled?: unknown; refund?: unknown; closes_before_minutes?: unknown };
type Raw = { enabled?: unknown; edit?: unknown; cancel?: unknown };
const objectOf = <T>(value: unknown): T | null => (value && typeof value === "object" && !Array.isArray(value) ? value as T : null);
const minutesOf = (value: unknown, fallback: number) => {
  const minutes = Number(value ?? fallback);
  return Number.isInteger(minutes) && minutes >= 0 ? Math.min(minutes, 43200) : fallback;
};

export const selfServiceOf = (org: unknown, event?: unknown): SelfServiceSettings => {
  const o = objectOf<Raw>(org) ?? {}, e = objectOf<Raw>(event) ?? {};
  const edit = { ...objectOf<RawAction>(o.edit), ...objectOf<RawAction>(e.edit) };
  const cancel = { ...objectOf<RawAction>(o.cancel), ...objectOf<RawAction>(e.cancel) };
  return {
    enabled: ({ ...o, ...e }).enabled === true,
    edit: { enabled: edit.enabled !== false, closes_before_minutes: minutesOf(edit.closes_before_minutes, 0) },
    cancel: { enabled: cancel.enabled === true, refund: cancel.refund === true, closes_before_minutes: minutesOf(cancel.closes_before_minutes, 10080) },
  };
};

export function useSelfService() {
  const supabase = useSupabaseClient<Database>();
  const userStore = useUserStore();
  const eventStore = useEventStore();

  const orgRaw = computed(() => (userStore.organization?.shop_settings as { self_service?: unknown } | null)?.self_service);
  const orgSettings = computed(() => selfServiceOf(orgRaw.value));
  const saveOrg = async (next: SelfServiceSettings) => {
    const org = userStore.organization;
    if (!org) return null;
    const shop_settings = { ...(org.shop_settings as Record<string, unknown> | null), self_service: next };
    const { error } = await supabase.from("organizations").update({ shop_settings: shop_settings as unknown as Json }).eq("id", org.id);
    if (!error) org.shop_settings = shop_settings as unknown as Json;
    return error;
  };

  // An activity follows its organization until it has rules of its own
  const event = computed(() => eventStore.current_event);
  const eventRaw = computed(() => (event.value?.additional_settings as { self_service?: unknown } | null)?.self_service);
  const eventOwn = computed(() => objectOf(eventRaw.value) != null);
  const eventSettings = computed(() => selfServiceOf(orgRaw.value, eventRaw.value));
  const saveEvent = async (next: SelfServiceSettings | null) => {
    if (!event.value) return null;
    const all = { ...(event.value.additional_settings as Record<string, unknown>) };
    if (next) all.self_service = next;
    else delete all.self_service;
    const { error } = await supabase.from("events").update({ additional_settings: all as Json }).eq("id", event.value.id);
    if (error) return error;
    //@ts-ignore additional_settings is jsonb
    event.value.additional_settings = all;
    return null;
  };

  return { orgSettings, saveOrg, eventOwn, eventSettings, saveEvent };
}
